import { FC } from "react";
import { useGetRelatedPosts } from "../../functions/blogHook";
import { useNavigate } from "react-router-dom";

interface RelatedPost {
  _id: string;
  image: string;
  title: string;
  excerpt: string;
  authorSpecialty: string;
  category: string;
}

interface RelatedPostsProps {
  blogId: string;
}

const RelatedPosts: FC<RelatedPostsProps> = ({ blogId }) => {
  const { relatedPosts, isLoading, isError, error } = useGetRelatedPosts(blogId);
  const navigate = useNavigate();

  const handlePostClick = (postId: string) => {
    navigate(`/post/${postId}`);
  };

  return (
    <section id="related" className="mt-20 py-12 border-t border-gray-100">
      <div className="mb-10">
        <h2 className="text-3xl font-bold text-gray-900 mb-2">
          <span className="bg-gradient-to-r from-indigo-600 to-blue-500 bg-clip-text text-transparent">
            Related
          </span>{" "}
          Articles
        </h2>
        <p className="text-gray-500">Discover more content in this category</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {isLoading &&
          Array.from({ length: 3 }).map((_, index) => (
            <div key={index} className="animate-pulse">
              <div className="rounded-xl bg-gradient-to-br from-gray-50 to-gray-100 w-full h-60 mb-4"></div>
              <div className="h-5 bg-gray-100 rounded-full w-3/4 mb-3"></div>
              <div className="h-4 bg-gray-100 rounded-full w-full mb-2"></div>
              <div className="h-4 bg-gray-100 rounded-full w-2/3"></div>
            </div>
          ))}

{!isLoading && Array.isArray(relatedPosts) && relatedPosts.length > 0 ? (
          relatedPosts.map((relatedPost: RelatedPost) => (
            <article
              key={relatedPost._id}
              className="group relative cursor-pointer transform transition-all duration-300 hover:-translate-y-1.5"
              onClick={() => handlePostClick(relatedPost._id)}
            >
              <div className="rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow duration-300">
                <div className="relative aspect-video">
                  <img
                    src={relatedPost.image}
                    alt={relatedPost.title}
                    className="w-full h-60 object-cover transform transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-gray-900/40 via-transparent to-transparent" />
                  <div className="absolute top-4 right-4">
                    <span className="px-3 py-1.5 text-xs font-medium bg-indigo-100 text-indigo-800 rounded-full">
                      {relatedPost.category}
                    </span>
                  </div>
                </div>

                <div className="p-6 bg-white">
                  <h3 className="text-1xl font-semibold text-gray-900 mb-3 group-hover:text-indigo-600 transition-colors">
                    {relatedPost.title}
                  </h3>
                  <p className="text-gray-600 mb-4 line-clamp-2 leading-relaxed">
                    {relatedPost.excerpt}
                  </p>
                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2">
                      <svg 
                        className="w-4 h-4 text-gray-400" 
                        fill="none" 
                        stroke="currentColor" 
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                      <span className="text-gray-500">{relatedPost.authorSpecialty}</span>
                    </div>
                  </div>
                </div>
              </div>
            </article>
          ))
        ) : (
          <div className="col-span-1 md:col-span-2 lg:col-span-3 text-center text-gray-500">
            No related posts available.
          </div>
        )}
      </div>

      {isError && (
        <div className="mt-8 p-4 bg-red-50 rounded-xl border border-red-100">
          <div className="flex items-center gap-3 text-red-600">
            <svg 
              className="w-5 h-5" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <span className="font-medium">Error loading related posts: {error?.message || "Unknown error"}</span>
          </div>
        </div>
      )}
    </section>
  );
};

export default RelatedPosts;